import React from "react";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import Feature from "@insightfulscience/atomic-react/Feature";
import Button from "@insightfulscience/atomic-react/Button";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";

import { useLocale, WithLocale } from "@insightfulscience/i18";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "@src/components/SiteLayout";
import TryForFree from "@src/components/TryForFree";
import routes from "@src/routes";
import WithErrorHandling from "@src/components/AppError/WithErrorHandling";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Image from "@insightfulscience/atomic-react/Image";
import Link from "@insightfulscience/atomic-react/Link";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Heading from "@insightfulscience/atomic-react/Heading";
import Block from "@insightfulscience/atomic-react/Block";
import SmartTabs from "@insightfulscience/atomic-react/utils/SmartTabs";
import { TabCol } from "@insightfulscience/atomic-react/Tabs/Grid";
import Tabs from "@insightfulscience/atomic-react/Tabs";
import TabTitle from "@insightfulscience/atomic-react/Tabs/Title";
import Icon from "@insightfulscience/atomic-react/Icon";
import PreloadImages from "@src/components/PreloadImages";
import resources from "./locales";

const heroImage =
	"/images/dotmatics/omiq/home/hero-flow-cytometry-analysis.gif";
const customAutogatingImage =
	"/images/dotmatics/omiq/home/custom-autogating.png";
const omiqLumaImage = "/images/dotmatics/omiq/home/omiq-luma.png";
const collaboratorsConnectionsImage =
	"/images/dotmatics/omiq/home/collaborators-connections.png";
const lumaImage = "/images/common/companies/luma-logo.svg";
const easyPanelImage = "/images/common/companies/easy-panel-logo.svg";
const prismImage = "/images/common/companies/prism-logo.svg";
const classicalAnalysisImage =
	"/images/dotmatics/omiq/home/classical-analysis.png";
const highDimensionalAnalysisImage =
	"/images/dotmatics/omiq/home/high-dimensional-analysis.png";
const integrationsEasyPanelImage =
	"/images/dotmatics/omiq/home/integrations-EasyPanel.png";
const enterpriseImage = "/images/dotmatics/omiq/home/enterprise.png";
const metadataImage = "/images/dotmatics/omiq/home/metadata.png";
const workflowImage = "/images/dotmatics/omiq/home/workflow.png";
const rocheLogoImage = "/images/common/companies/roche-logo.svg";
const dkfzLogoImage = "/images/common/companies/dkfz-logo.svg";
const modernaLogoImage = "/images/common/companies/moderna-logo.svg";
const laJollaInstituteLogoImage =
	"/images/common/companies/laJollaInstitute.svg";
const sanofiLogoImage = "/images/common/companies/sanofi-logo.svg";
const johnsHopkinsUniversity =
	"/images/common/companies/johns-hopkins-university.png";
const stanfordLogoImage =
	"/images/common/companies/stanford-university-logo.svg";
const bristolMyersLogoImage =
	"/images/common/companies/bristol-myers-squibb-logo.svg";
const sorbonneLogoImage =
	"/images/common/companies/sorbonne-university-logo.svg";
const novartisLogoImage = "/images/common/companies/novartis-logo.svg";
const utSouthWesternLogoImage = "/images/common/companies/utSouthWestern.svg";
const genentechLogoImage = "/images/common/companies/genentech-logo.svg";
const leidenLogoImage = "/images/common/companies/leiden-university.svg";
const genmabLogoImage = "/images/common/companies/genmab-logo.svg";
const insermLogoImage = "/images/common/companies/inserm-logo.svg";
const merckLogoImage = "/images/common/companies/merck-logo.svg";
const uclaLogoImage = "/images/common/companies/ucla-logo.svg";

const logos = [
	rocheLogoImage,
	dkfzLogoImage,
	modernaLogoImage,
	laJollaInstituteLogoImage,
	sanofiLogoImage,
	johnsHopkinsUniversity,

	stanfordLogoImage,
	bristolMyersLogoImage,
	sorbonneLogoImage,
	novartisLogoImage,
	utSouthWesternLogoImage,

	genentechLogoImage,
	leidenLogoImage,
	genmabLogoImage,
	insermLogoImage,
	merckLogoImage,
	uclaLogoImage,
];

const Home: React.FC = () => {
	const [t] = useLocale("home-page");

	return (
		<SiteLayout>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PreloadImages
					imagesHref={[
						"/images/dotmatics/omiq/home/hero-flow-cytometry-analysis.gif",
						"/images/common/companies/luma-logo.svg",
						"/images/common/companies/easy-panel-logo.svg",
						"/images/common/companies/prism-logo.svg",
					]}
				/>
			</PageMeta>
			<Hero color="white">
				<HeroTitle>Flow Cytometry Software, Reimagined</HeroTitle>
				<HeroDescription>
					Streamline your flow cytometry analysis with innovative, cloud-based
					software
				</HeroDescription>
				<HeroAction>
					<HeroButtonLink
						href={routes.freeTrial.url}
						styleType="secondary"
						data-qa-selector="freeTrialHero"
					>
						Try for Free
					</HeroButtonLink>
					<HeroButtonLink
						href={routes.pricing.url}
						isOutline
						data-qa-selector="pricingHero"
					>
						Pricing
					</HeroButtonLink>
				</HeroAction>
				<Row spacing={{ mt: 10 }}>
					<Col>
						<Image src={heroImage} />
					</Col>
				</Row>
			</Hero>
			<LandingSimpleSection spacing={{ py: 0 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Row>
						<Col>
							<Paragraph
								sx={{ textAlign: "center", fontSize: "l" }}
								spacing={{ mt: 0, mb: 10 }}
							>
								End-to-End Analysis with Seamless Integrations
							</Paragraph>
						</Col>
					</Row>
					<Row px>
						<Col md="auto" lg="auto" spacing={{ mb: 6, md: { mb: 0 } }}>
							<Block
								spacing={{ py: 5, px: 5 }}
								utility={{
									radius: 8,
									border: { width: 1, color: "dotmatics-1" },
								}}
								sizing={{ h: "full" }}
							>
								<Block spacing={{ mb: 4 }}>
									<Link href={routes.solutions.subRoutes.omiqLuma.url}>
										<Image src={lumaImage} sizing={{ h: 10 }} />
									</Link>
								</Block>

								<Paragraph>
									Directly import data from your flow cytometers to OMIQ.{" "}
									<Link href={routes.solutions.subRoutes.omiqLuma.url}>
										Learn more.
									</Link>
								</Paragraph>
							</Block>
						</Col>
						<Col md="auto" lg="auto" spacing={{ mb: 6, md: { mb: 0 } }}>
							<Block
								spacing={{ py: 5, px: 5 }}
								utility={{
									radius: 8,
									border: { width: 1, color: "dotmatics-1" },
								}}
								sizing={{ h: "full" }}
							>
								<Block spacing={{ mb: 4 }}>
									<Link
										href={routes.product.subRoutes.easyPanelIntegration.url}
									>
										<Image src={easyPanelImage} sizing={{ h: 10 }} />
									</Link>
								</Block>
								<Paragraph>
									Automatically validate your spectral signatures.{" "}
									<Link
										href={routes.product.subRoutes.easyPanelIntegration.url}
									>
										Learn more.
									</Link>
								</Paragraph>
							</Block>
						</Col>
						<Col md="auto" lg="auto">
							<Block
								spacing={{ py: 5, px: 5 }}
								utility={{
									radius: 8,
									border: { width: 1, color: "dotmatics-1" },
								}}
								sizing={{ h: "full" }}
							>
								<Block spacing={{ mb: 4 }}>
									<Link href={routes.product.subRoutes.prismIntegration.url}>
										<Image src={prismImage} sizing={{ h: 10 }} />
									</Link>
								</Block>
								<Paragraph>
									Effortlessly transfer your data to Prism with no manual
									reshaping.{" "}
									<Link href={routes.product.subRoutes.prismIntegration.url}>
										Learn more.
									</Link>
								</Paragraph>
							</Block>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>

			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Title>
					Modern Cloud Software for Classical and Advanced Flow Cytometry
					Analysis
				</LandingSimpleSection.Title>
				<LandingSimpleSection.DescriptionV2>
					Complete your entire workflow in one software, from raw data to
					statistical significance, using classic flow cytometry tools
					seamlessly blended with the latest algorithms and visualizations
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Feature px={false}>
						<FeatureImage lg={8} src={classicalAnalysisImage} />
						<FeatureMeta lg={4} indent>
							<FeatureMeta.Label type="default" spacing={{ mb: 4 }}>
								Classical Analysis
							</FeatureMeta.Label>
							<FeatureMeta.Title>
								Save Time Performing Classical Flow Analysis
							</FeatureMeta.Title>
							<FeatureMeta.Description>
								Access your work from anywhere, collaborate with your team, and
								run reproducible workflows on a clean, clear, user-friendly
								interface.
							</FeatureMeta.Description>
							<FeatureMeta.Description>
								Perform all your essential flow cytometry analysis including
								compensation and unmixing, scaling, gating, report building,
								figures and graphs.
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link href={routes.product.subRoutes.classicAnalysis.url}>
									Classical Analysis <Icon name="angle-right" />
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Feature px={false}>
						<FeatureMeta indent lg={4}>
							<FeatureMeta.Label type="default" spacing={{ mb: 4 }}>
								High Dimensional Analysis
							</FeatureMeta.Label>
							<FeatureMeta.Title>
								Cutting-Edge Algorithms and Visualizations
							</FeatureMeta.Title>
							<FeatureMeta.Description>
								Take your research to the next level with over 30 natively
								integrated high dimensional algorithms for data cleaning,
								normalization, dimension reduction, clustering, differential
								expression and trajectory / pseudotime inference.
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link
									href={routes.product.subRoutes.highDimensionalAnalysis.url}
								>
									High Dimensional Analysis <Icon name="angle-right" />
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage lg={8} src={highDimensionalAnalysisImage} />
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Feature px={false}>
						<FeatureImage lg={8} src={collaboratorsConnectionsImage} />
						<FeatureMeta lg={4} indent>
							<FeatureMeta.Label type="default" spacing={{ mb: 4 }}>
								Cloud Platform
							</FeatureMeta.Label>
							<FeatureMeta.Title>
								Work and Collaborate in the Cloud
							</FeatureMeta.Title>
							<FeatureMeta.Description>
								Remove machine limitations with secure cloud storage and
								computation. Analyze large, complex datasets without crashes or
								lagging.{" "}
							</FeatureMeta.Description>
							<FeatureMeta.Description>
								Easily share workflows, datasets, reports, and results, with
								individual users or groups.
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link href={routes.product.subRoutes.cloudPlatform.url}>
									Cloud Platform <Icon name="angle-right" />
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Feature px={false}>
						<FeatureMeta indent lg={4}>
							<FeatureMeta.Label type="default" spacing={{ mb: 4 }}>
								Integrations
							</FeatureMeta.Label>
							<FeatureMeta.Title>
								Seamless Integrations and Automation
							</FeatureMeta.Title>
							<FeatureMeta.Description>
								Enable end-to-end analysis with integrations for EasyPanel,
								GraphPad Prism, and Luma, facilitating easy data import and
								export, automated panel design, integrated analysis, and reduced
								data handling.{" "}
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link href={routes.product.subRoutes.dataManagement.url}>
									Integrations <Icon name="angle-right" />
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage lg={8} src={integrationsEasyPanelImage} />
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Block sx={{ textAlign: "center" }}>
						<Button.Link styleType="secondary" href={routes.freeTrial.url}>
							Try OMIQ Free
						</Button.Link>
					</Block>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>

			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Title>
					Where Flow Cytometry Meets Data Science
				</LandingSimpleSection.Title>
				<LandingSimpleSection.DescriptionV2>
					OMIQ helps make sense of the chaos of cytometry analysis with
					workflows and metadata providing clear visual touchpoints and reducing
					manual data handling and its associated risks.{" "}
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Row px>
						<Col md="auto" lg="auto" spacing={{ mb: 6, md: { mb: 0 } }}>
							<Block
								spacing={{ py: 6, px: 6 }}
								utility={{
									radius: 8,
									border: { width: 1, color: "dotmatics-1" },
								}}
								sizing={{ h: "full" }}
							>
								<Heading Tag="h3" sx={{ fontSize: "l" }}>
									Metadata Annotation
								</Heading>
								<Paragraph spacing={{ mb: 4 }}>
									Concatenate, gate, group, filter, or sort by metadata to
									transform your raw flow cytometry data into valuable,
									interpretable information.
								</Paragraph>
								<Block>
									<Image src={metadataImage} />
								</Block>
							</Block>
						</Col>
						<Col md="auto" lg="auto">
							<Block
								spacing={{ py: 6, px: 6 }}
								utility={{
									radius: 8,
									border: { width: 1, color: "dotmatics-1" },
								}}
								sizing={{ h: "full" }}
							>
								<Heading Tag="h3" sx={{ fontSize: "l" }}>
									Intuitive Workflows
								</Heading>
								<Paragraph spacing={{ mb: 4 }}>
									Workflows provide a step-by-step view of all analysis tasks.
									Create and configure workflow templates to automate your
									analysis and enable sharing, versioning, and reproducibility.
								</Paragraph>
								<Block>
									<Image src={workflowImage} />
								</Block>
							</Block>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection spacing={{ pb: 0 }} isBorderNone>
				<LandingSimpleSection.Title>
					From Ideas to Insights, Faster
				</LandingSimpleSection.Title>
				<LandingSimpleSection.DescriptionV2>
					Remove the data bottleneck with custom solutions designed to scale
					your analysis pipelines
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<SmartTabs initialActiveTab="enterprise">
						<Tabs isBorderBottom={false} mx>
							<TabCol md="auto">
								<SmartTabs.Tab id="enterprise" Component={TabTitle}>
									Enterprise
								</SmartTabs.Tab>
							</TabCol>
							<TabCol md="auto">
								<SmartTabs.Tab id="customAutogating" Component={TabTitle}>
									Custom Autogating
								</SmartTabs.Tab>
							</TabCol>
							<TabCol md="auto">
								<SmartTabs.Tab id="omiqLuma" Component={TabTitle}>
									OMIQ Luma
								</SmartTabs.Tab>
							</TabCol>
						</Tabs>
						<Row spacing={{ mt: 10 }} className="tabs-content">
							<SmartTabs.TabBody.Mounted id="enterprise" Component={Col}>
								<Feature px={false}>
									<FeatureImage lg={8} src={enterpriseImage} />
									<FeatureMeta lg={4} indent={false}>
										<FeatureMeta.Title>
											Drive Analysis at Scale
										</FeatureMeta.Title>
										<FeatureMeta.Description>
											Bring your entire organization together on dedicated
											infrastructure that&apos;s fast, scalable, and secure.
										</FeatureMeta.Description>
										<FeatureMeta.Description>
											Unlock limitless potential with optimized processes, and
											advanced automation and customizations, all backed by
											premium support.{" "}
										</FeatureMeta.Description>
										<FeatureMeta.Action>
											<Link href={routes.solutions.subRoutes.enterprise.url}>
												Learn More <Icon name="angle-right" />
											</Link>
										</FeatureMeta.Action>
									</FeatureMeta>
								</Feature>
							</SmartTabs.TabBody.Mounted>
							<SmartTabs.TabBody.Mounted id="customAutogating" Component={Col}>
								<Feature px={false}>
									<FeatureImage lg={8} src={customAutogatingImage} />
									<FeatureMeta lg={4} indent={false}>
										<FeatureMeta.Title>
											Best-in-Class Automated Gating
										</FeatureMeta.Title>
										<FeatureMeta.Description>
											Custom automated gating pipelines trained on your data,
											for your use. Replace time-costly and subjective manual
											gating to provide rapid, robust and reproducible results.
										</FeatureMeta.Description>
										<FeatureMeta.Action>
											<Link
												href={routes.solutions.subRoutes.customAutogating.url}
											>
												Learn More <Icon name="angle-right" />
											</Link>
										</FeatureMeta.Action>
									</FeatureMeta>
								</Feature>
							</SmartTabs.TabBody.Mounted>
							<SmartTabs.TabBody.Mounted id="omiqLuma" Component={Col}>
								<Feature px={false}>
									<FeatureImage lg={8} src={omiqLumaImage} />
									<FeatureMeta lg={4} indent={false}>
										<FeatureMeta.Title>
											Unlock the Full Potential of Your Flow Cytometry Data
										</FeatureMeta.Title>
										<FeatureMeta.Description>
											Transform your flow cytometry data management with OMIQ
											Luma. Streamline every step of your data journey, from
											instrument to insight, with automated uploads, end-to-end
											traceability, and integrated analysis.
										</FeatureMeta.Description>
										<FeatureMeta.Action>
											<Link href={routes.solutions.subRoutes.omiqLuma.url}>
												Learn More <Icon name="angle-right" />
											</Link>
										</FeatureMeta.Action>
									</FeatureMeta>
								</Feature>
							</SmartTabs.TabBody.Mounted>
						</Row>
					</SmartTabs>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<LandingSimpleSection isBorderNone>
				<LandingSimpleSection.Body>
					<Row>
						<Col>
							<Heading
								Tag="h2"
								size="l"
								sx={{ textAlign: "center" }}
								spacing={{ mb: 10 }}
							>
								Trusted by the world’s leading research organizations
							</Heading>
						</Col>
					</Row>
					<Block utility={{ lg: { display: "none" } }}>
						<Row
							px
							smCols={2}
							mdCols={4}
							lgCols={6}
							utility={{ align: { items: "center" }, justify: "center" }}
						>
							{logos.map((logo, index) => (
								<Col
									key={Math.random()}
									spacing={{ mb: index === logos.length - 1 ? 0 : 8 }}
								>
									<Image src={logo} sizing={{ w: "full" }} />
								</Col>
							))}
						</Row>
					</Block>
					<Block utility={{ display: "none", lg: { display: "block" } }}>
						<Row
							px
							smCols={2}
							mdCols={4}
							lgCols={6}
							utility={{ align: { items: "center" }, justify: "center" }}
						>
							{logos.slice(0, 6).map((logo) => (
								<Col key={Math.random()} spacing={{ mb: 16 }}>
									<Block spacing={{ px: 4 }}>
										<Image src={logo} sizing={{ w: "full" }} />
									</Block>
								</Col>
							))}
						</Row>
						<Row
							px
							smCols={2}
							mdCols={4}
							lgCols={6}
							utility={{ align: { items: "center" }, justify: "center" }}
						>
							{logos.slice(6, 11).map((logo) => (
								<Col key={Math.random()} spacing={{ mb: 16 }}>
									<Block spacing={{ px: 4 }}>
										<Image src={logo} sizing={{ w: "full" }} />
									</Block>
								</Col>
							))}
						</Row>
						<Row
							px
							smCols={2}
							mdCols={4}
							lgCols={6}
							utility={{ align: { items: "center" }, justify: "center" }}
						>
							{logos.slice(11).map((logo) => (
								<Col key={Math.random()}>
									<Block spacing={{ px: 4 }}>
										<Image src={logo} sizing={{ w: "full" }} />
									</Block>
								</Col>
							))}
						</Row>
					</Block>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>

			<TryForFree />
		</SiteLayout>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default pipe(Home, WithLocale({ resources }), WithErrorHandling as any);
